'use client';

/**
 * TurnActivityBlock — ONE collapsible strip for everything a turn did before it
 * answered: tool runs, reasoning, and the narration between them.
 *
 * It replaces ToolActivityGroup, which framed only tool runs. That was the
 * smaller half of the problem: a turn that alternated "here's what I'll do" →
 * tools → "now this" → tools produced a bordered card per run PLUS a full prose
 * block per narration PLUS a bordered disclosure per reasoning chunk. Measured
 * on a real transcript: 33 framed blocks and ~1,600px of chrome for one answer.
 *
 * Contract:
 * - Open while the turn streams (progress must be visible), auto-collapses ONCE
 *   when the work finishes; after that the user's choice is never overridden.
 * - An error keeps it open — a failure must not hide behind a chevron.
 * - Stream order is preserved inside; rows are flat (no nested card frames).
 * - The FINAL answer is never in here (see `buildTurnLayout`).
 */

import React, { useEffect, useRef, useState } from 'react';
import { CheckCircleIcon, ChevronDownIcon, Loader2Icon, XCircleIcon } from 'lucide-react';
import { MemoizedMarkdown } from '../MemoizedMarkdown.js';
import { ToolBlock } from './ToolBlock.js';
import { ReasoningBlock } from './ReasoningBlock.js';
import { getToolLabel } from '../tool-display/tool-labels.js';
import { isTerminalStatus, resolveToolStatus } from '../tool-display/tool-status.js';
import { resolveToolName, type TimelineSegment } from '../utils/messageTimeline.js';
import type { ToolInvocation } from '../utils/toolInvocations.js';

export interface TurnActivityBlockProps {
  /** Ordered narration / reasoning / tool-run segments for this stretch of work. */
  segments: readonly TimelineSegment[];
  /** True while this is the live tail of the actively-streaming message. */
  isStreaming: boolean;
  /** Message id, for stable markdown keys. */
  idPrefix: string;
}

export interface ActivityStats {
  invocations: ToolInvocation[];
  doneCount: number;
  hasError: boolean;
  /** First invocation that has not reached a terminal state, if any. */
  running: ToolInvocation | null;
  reasoningCount: number;
  narrationCount: number;
}

function summarize(segments: readonly TimelineSegment[]): ActivityStats {
  const invocations: ToolInvocation[] = [];
  let doneCount = 0;
  let hasError = false;
  let running: ToolInvocation | null = null;
  let reasoningCount = 0;
  let narrationCount = 0;

  for (const segment of segments) {
    if (segment.kind === 'tools') {
      for (const invocation of segment.invocations) {
        invocations.push(invocation);
        const status = resolveToolStatus(invocation.toolCall, invocation.result);
        if (status === 'error') hasError = true;
        if (isTerminalStatus(status)) doneCount += 1;
        else if (!running) running = invocation;
      }
    } else if (segment.kind === 'reasoning') {
      reasoningCount += 1;
    } else if (segment.kind === 'text') {
      narrationCount += 1;
    }
  }

  return { invocations, doneCount, hasError, running, reasoningCount, narrationCount };
}

/** What the strip says about itself, collapsed. */
export function describeActivity(stats: ActivityStats, allDone: boolean): string {
  if (!allDone) {
    if (stats.running) {
      const part = stats.running.toolCall;
      const label = getToolLabel(resolveToolName(part) || 'tool', part.input ?? part.args);
      return label.subject ? `${label.running} · ${label.subject}…` : `${label.running}…`;
    }
    return stats.reasoningCount > 0 ? 'Thinking…' : 'Working…';
  }
  const toolCount = stats.invocations.length;
  if (toolCount > 0) {
    return `Worked through ${toolCount} step${toolCount === 1 ? '' : 's'}`;
  }
  if (stats.reasoningCount > 0) return 'Thought it through';
  return 'Working notes';
}

export function TurnActivityBlock({
  segments,
  isStreaming,
  idPrefix,
}: TurnActivityBlockProps) {
  const stats = summarize(segments);
  const allDone = stats.doneCount === stats.invocations.length && !isStreaming;

  const [isOpen, setIsOpen] = useState(!allDone || stats.hasError);
  const collapsedOnFinish = useRef(false);
  useEffect(() => {
    if (allDone && !stats.hasError && !collapsedOnFinish.current) {
      collapsedOnFinish.current = true;
      setIsOpen(false);
    }
  }, [allDone, stats.hasError]);

  if (segments.length === 0) return null;

  const headerLabel = describeActivity(stats, allDone);
  const headerIcon = !allDone ? (
    <Loader2Icon className="size-3.5 animate-spin text-primary" aria-label="working" />
  ) : stats.hasError ? (
    <XCircleIcon className="size-3.5 text-destructive" aria-label="finished with errors" />
  ) : (
    <CheckCircleIcon className="size-3.5 text-green-600 dark:text-green-500" aria-label="finished" />
  );

  // Count shown on the right: steps while working, a quiet receipt after.
  const countLabel = !allDone
    ? `${stats.doneCount} of ${stats.invocations.length || 1} steps`
    : [
        stats.invocations.length > 0
          ? `${stats.invocations.length} tool${stats.invocations.length === 1 ? '' : 's'}`
          : null,
        stats.narrationCount > 0 ? `${stats.narrationCount} note${stats.narrationCount === 1 ? '' : 's'}` : null,
      ]
        .filter(Boolean)
        .join(' · ');

  const lastSegmentKey = segments[segments.length - 1]?.key;

  return (
    <div
      data-testid="turn-activity"
      className={`mt-3 rounded-lg border overflow-hidden ${
        stats.hasError ? 'border-destructive/40' : 'border-border'
      }`}
    >
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full min-h-9 flex items-center gap-2 px-3 py-2 hover:bg-muted/50 transition-colors text-left"
        aria-expanded={isOpen}
      >
        <span className="flex-shrink-0">{headerIcon}</span>
        <span className="text-sm font-medium min-w-0 truncate">{headerLabel}</span>
        <span className="ml-auto flex items-center gap-2 flex-shrink-0 text-xs text-muted-foreground tabular-nums">
          {countLabel && <span>{countLabel}</span>}
          <ChevronDownIcon
            className={`size-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        </span>
      </button>

      {isOpen && (
        <div className="border-t border-border/60 divide-y divide-border/60">
          {segments.map((segment) => {
            const isLive = isStreaming && segment.key === lastSegmentKey;

            if (segment.kind === 'text') {
              // Interim narration: small and muted. It explains the work; it is
              // not the answer, and giving it answer-weight is what made a turn
              // read as several competing answers.
              return (
                <div
                  key={segment.key}
                  data-testid="activity-note"
                  className="px-3 py-2 text-xs text-muted-foreground [&_p]:my-0"
                >
                  <MemoizedMarkdown id={`${idPrefix}-${segment.key}`} content={segment.text} />
                </div>
              );
            }

            if (segment.kind === 'reasoning') {
              return (
                <div key={segment.key} className="[&>div]:mt-0 [&>div]:rounded-none [&>div]:border-0">
                  <ReasoningBlock text={segment.text} isStreaming={isLive} />
                </div>
              );
            }

            if (segment.kind === 'tools') {
              return (
                <React.Fragment key={segment.key}>
                  {segment.invocations.map((invocation) => {
                    const part =
                      invocation.toolCall && Object.keys(invocation.toolCall).length > 0
                        ? invocation.toolCall
                        : invocation.result;
                    if (!part) return null;
                    return (
                      <ToolBlock
                        key={invocation.toolCallId}
                        part={part}
                        pairedResult={invocation.result}
                        defaultOpen={false}
                        variant="row"
                      />
                    );
                  })}
                </React.Fragment>
              );
            }

            return null;
          })}
        </div>
      )}
    </div>
  );
}
